import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { useEffect, useState } from 'react';

import { AttractionsDataModel, AttractionsFirestoreResponse } from '../../types';
import { ResponseSelectDataProps, useGetAdminUsers } from '../usersQuery';
import { fetchPaginatedCollectionRealtime } from '../utils';

export const useGetAttractionsFromFirestore = (
  page?: number,
  elements?: number,
  options?: UseQueryOptions<AttractionsDataModel, FirebaseError, AttractionsDataModel>,
): UseQueryResult<AttractionsDataModel, FirebaseError> => {
  const queryClient = useQueryClient();
  const subscriptionKey = ['attractions', page ?? 1, elements ?? 20000];

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<FirebaseError | null>(null);

  const { data: adminUsers, isLoading: isAdminLoading } = useGetAdminUsers();

  useEffect(() => {
    if (isAdminLoading || !adminUsers) return;

    const unsubscribePromise = fetchPaginatedCollectionRealtime<AttractionsFirestoreResponse>(
      'attractions',
      page ?? 1,
      elements ?? 5000,
      'createdAt',
      async (data, totalPages) => {
        const mappedData = data.map((item) => {
          const createdByName = adminUsers
          // eslint-disable-next-line no-return-assign
            ?.find((e: ResponseSelectDataProps) => e.label === item.createdBy);
          return {
            id: item.id,
            title: item.title,
            address: item.address,
            city: item.city,
            createdAt: item.createdAt,
            thumbnail: item.thumbnail,
            translationStatus: item.translationStatus,
            type: item.type,
            createdBy: createdByName?.key || '',
            status: item?.status || null,
            price: item?.price || 0,
            currency: item?.currency || 'PLN',
            email: item?.email || '',
            duration: item?.duration || 1,
            languages: item.languages,
          };
        });

        setIsLoading(false);
        queryClient.setQueryData<AttractionsDataModel>(subscriptionKey, {
          data: mappedData,
          totalPages,
          isLoading: false,
          error: null,
        });
      },
      (e) => {
        setError(e as FirebaseError);
        setIsLoading(false);
      },
    );

    // eslint-disable-next-line consistent-return
    return () => {
      unsubscribePromise
        .then((unsubscribe) => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        })
        .catch((e) => {
          setError(e as FirebaseError);
          setIsLoading(false);
        });
    };
  }, [page, elements, queryClient, adminUsers, isAdminLoading]);

  return useQuery<AttractionsDataModel, FirebaseError, AttractionsDataModel>({
    queryKey: subscriptionKey,
    queryFn: async () => {
      const cachedData = queryClient.getQueryData<AttractionsDataModel>(subscriptionKey);
      if (cachedData) {
        return cachedData;
      }
      return { data: [], totalPages: 0, isLoading, error };
    },
    enabled: !isAdminLoading, // <-- ważne, zapobiega wcześniejszemu uruchomieniu
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};
